/**
 * 청크 업로드 순차 큐 (설계 §5.4).
 *
 * ⚠️ 이 모듈은 **의도적으로 import 가 하나도 없다.** 원래 이 루프는 RecordEdit.js 안의 모듈
 * private 함수였는데, 그 파일은 `api/Resource → myNet → query-string`(ESM) 을 끌어와 테스트에
 * mock 벽 20개가 필요하고(`__tests__/RecordEdit.test.js`, lint 부채 23건이라 수정 불가) 그래서
 * "호출처가 이 훅을 실제로 호출하는가"를 고정할 방법이 없었다. leaf 로 분리하면 mock 0개로
 * 호출처를 테스트할 수 있고, RecordEdit 에는 새 전이 간선이 생기지 않아 기존 테스트가 안전하다.
 * (planning-rules 근본원인2 — 등록처·호출처·소비 동작을 각각 테스트로 고정)
 */

/**
 * 첨부들을 하나씩 순서대로 업로드한다.
 *
 * 순차인 이유: `uploadChunked` 자체가 파트를 3개씩 동시에 올리므로(PART_CONCURRENCY) 파일까지
 * 병렬로 돌리면 동시 전송량이 곱해져 대용량에서 메모리·대역폭이 함께 튄다.
 *
 * 대기 표시: 시작 시 전원을 대기로 켜고 각자의 차례에 끈다. 이 표시가 없으면 순차 업로드의
 * 2번째 파일이 아무 변화 없이 방치돼 사용자가 "멈췄나?" 로 읽는다.
 *
 * @param {Array<{uploadChunkedTo: Function, onUploadQueued?: Function}>} attachments
 * @param {string} resourceId
 * @returns {Promise<Array<object>>} 입력 순서대로의 업로드 결과
 */
export const runChunkedUploadsSequentially = async (attachments, resourceId) => {
  const list = attachments || []
  // mock attachment·구 경로 객체에는 훅이 없을 수 있다
  const notify = (attachment, queued) => {
    if (attachment != null && typeof attachment.onUploadQueued === 'function') {
      attachment.onUploadQueued(queued)
    }
  }

  // 이미 해제한 대상에 다시 false 를 보내지 않는다 — 무해하지만 불필요한 리렌더가 된다
  const released = new Set()
  const release = (attachment) => {
    if (released.has(attachment)) return
    released.add(attachment)
    notify(attachment, false)
  }

  list.forEach((attachment) => notify(attachment, true))

  try {
    const results = []
    for (let i = 0; i < list.length; i += 1) {
      const attachment = list[i]
      release(attachment)
      // 순차 실행이 목적 — 병렬화하면 동시 전송량이 파일 수만큼 곱해진다
      // eslint-disable-next-line no-await-in-loop
      results.push(await attachment.uploadChunkedTo(resourceId))
    }
    return results
  } finally {
    // 중간 실패 시 뒤 파일들이 "대기 중" 에 영구히 갇히지 않게 남은 전원을 해제한다
    list.forEach(release)
  }
}

export default { runChunkedUploadsSequentially }
